import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
  Conflict,
  CoreApi,
  ProductEntity,
  UnavailableProductComponent,
  ReplacedFrameComponent
} from '@inter-ikea-storageone/range';
import { RootState } from 'ducks';
import { ThunkDispatch } from 'redux-thunk';
import { ComponentIds, Entity, HierarchyUtils } from '@inter-ikea-core/core';
import { showUnavailableFramesMenu } from 'ducks/sheets';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
import { showHelpLinesCollisionWarningSheet } from './helpLines';

export type CoreState = {
  selectedEntities: ProductEntity[];
  productEntities: ProductEntity[]; //Entities represented as products in the product sidebar
  shoppingBagEntities: ProductEntity[]; //Entities that should be added to the price/shoppinglist
  conflicts: Conflict[];
  invalidProducts: string[];
  invalidFrames: string[];
  replacedFrames: string[];
};

type SyncEntitiesPayload = {
  productEntities: ProductEntity[];
  shoppingBagEntities: ProductEntity[];
  selectedEntities: ProductEntity[];
};

type ResetEntitiesPayload = {
  productEntities: ProductEntity[];
  shoppingBagEntities: ProductEntity[];
};

// Reducer
export const initialState: CoreState = {
  selectedEntities: [],
  productEntities: [],
  shoppingBagEntities: [],
  conflicts: [],
  invalidProducts: [],
  invalidFrames: [],
  replacedFrames: []
};

const coreSlice = createSlice({
  name: 'CoreSlice',
  initialState,
  reducers: {
    selectEntity: (state, action) => {
      const payload = action.payload as ProductEntity;

      if (payload && !state.selectedEntities.find(entity => entity.entityId === payload.entityId)) {
        state.selectedEntities = [payload];
      }
    },
    deselectEntity: (state, action) => {
      state.selectedEntities = state.selectedEntities.filter(
        e => e.entityId !== action.payload?.entityId
      );
    },
    resetEntities: (state, action: PayloadAction<ResetEntitiesPayload>) => {
      state.selectedEntities = [];
      state.productEntities = action.payload.productEntities;
      state.shoppingBagEntities = action.payload.shoppingBagEntities;
      const entitiesIds = action.payload.productEntities.map(entity => entity.entityId);
      state.conflicts = state.conflicts.filter(conflict => entitiesIds.includes(conflict.entityId));
    },
    addConflicts: (state, action) => {
      state.conflicts = [...state.conflicts, ...action.payload];
    },
    removeConflict: (state, action) => {
      state.conflicts = state.conflicts.filter(conflict => conflict.entityId !== action.payload);
    },
    resetConflicts: state => {
      state.conflicts = [];
    },
    syncEntities: (state, action: PayloadAction<SyncEntitiesPayload>) => {
      state.productEntities = action.payload.productEntities;
      state.shoppingBagEntities = action.payload.shoppingBagEntities;
      state.selectedEntities = action.payload.selectedEntities;
    },
    addInvalidProducts: (state, action) => {
      state.invalidProducts = action.payload;
    },
    addInvalidFrames: (state, action) => {
      state.invalidFrames = action.payload;
    },
    addReplacedFrames: (state, action) => {
      state.replacedFrames = action.payload;
    }
  }
});

export const {
  selectEntity,
  deselectEntity,
  resetConflicts,
  addConflicts,
  addInvalidFrames,
  addInvalidProducts,
  addReplacedFrames,
  removeConflict,
  resetEntities,
  syncEntities
} = coreSlice.actions;

// Core EventBus subscriber
export const attachCoreSubscribers = (dispatch: ThunkDispatch<any, any, any>, coreApi: CoreApi) => {
  coreApi.subscribe('selected', entity => {
    if (entity) {
      dispatch(selectEntity(entity));
    }
  });

  coreApi.subscribe('deselected', entity => {
    dispatch(deselectEntity(entity));
  });

  coreApi.subscribe('load', () => {
    dispatch(
      resetEntities({
        productEntities: coreApi.entities.getAllProductEntities(),
        shoppingBagEntities: coreApi.entities.getAllShoppingBagEntities()
      })
    );
    dispatch(checkUnavailableProducts(coreApi));
  });

  coreApi.subscribe('conflict', (conflicts: Conflict[]) => {
    dispatch(addConflicts(conflicts));
  });

  coreApi.subscribe('loadVpc', () => {
    dispatch(showUnavailableFramesMenu(false));
  });

  coreApi.subscribe('history', () => {
    const entities = coreApi.getServices().sceneManager.entities;
    dispatch(checkReplacedFrames(entities, coreApi));

    const selectedEntities = entities.reduce((acc: ProductEntity[], entity: Entity) => {
      if (entity.hasComponent(ComponentIds.SELECTED_COMPONENT_ID)) {
        const productEntity = coreApi.entities.findProductEntity(entity.id);
        if (productEntity) {
          return [...acc, productEntity];
        }
      }
      return acc;
    }, [] as ProductEntity[]);

    dispatch(
      syncEntities({
        productEntities: coreApi.entities.getAllProductEntities(),
        shoppingBagEntities: coreApi.entities.getAllShoppingBagEntities(),
        selectedEntities
      })
    );
  });
  coreApi.subscribe('unavailableProductsUpdated', () => {
    dispatch(checkUnavailableProducts(coreApi));
  });
  coreApi.subscribe('helpLinesConflictsResolved', () => {
    dispatch(showHelpLinesCollisionWarningSheet(false));
  });
};

const checkReplacedFrames = (entities: Entity[], coreApi: CoreApi) => (
  dispatch: ThunkDispatch<any, any, any>
) => {
  const replacedNormalFrames = entities.filter(
    e =>
      e.hasComponent(ComponentIds.FRAME_COMPONENT_ID) && e.hasComponent(ReplacedFrameComponent.id)
  );
  const replacedFramesInCornerSolution = entities
    .filter(
      e => e.hasComponent(CornerSolutionComponent.id) && e.hasComponent(ReplacedFrameComponent.id)
    )
    .flatMap(cornerFrame =>
      HierarchyUtils.descendants(cornerFrame).filter(frame =>
        frame.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
      )
    );

  const replacedFrames = [...replacedNormalFrames, ...replacedFramesInCornerSolution].flatMap(
    frame => {
      const productEntity = coreApi.entities.findProductEntity(frame.id);
      return productEntity ? [productEntity.productId] : [];
    }
  );
  dispatch(addReplacedFrames(replacedFrames));
};

const checkUnavailableProducts = (coreApi: CoreApi) => (dispatch: ThunkDispatch<any, any, any>) => {
  const entities = coreApi.getServices().sceneManager.entities;
  const invalidProducts = entities
    .filter(e => e.hasComponent(UnavailableProductComponent.id))
    .flatMap(e => {
      const productEntity = coreApi.entities.findProductEntity(e.id);
      return productEntity ? [productEntity.productId] : [];
    });
  dispatch(addInvalidProducts(invalidProducts));

  const unavailableFramesInScene = entities.filter(
    e =>
      e.hasComponent(ComponentIds.FRAME_COMPONENT_ID) &&
      e.hasComponent(UnavailableProductComponent.id)
  );
  const unavailableFramesInCornerSolution = entities
    .filter(
      e =>
        e.hasComponent(CornerSolutionComponent.id) && e.hasComponent(UnavailableProductComponent.id)
    )
    .flatMap(cornerFrame =>
      HierarchyUtils.descendants(cornerFrame).filter(frame =>
        frame.hasComponent(ComponentIds.FRAME_COMPONENT_ID)
      )
    );
  const invalidFrames = [...unavailableFramesInScene, ...unavailableFramesInCornerSolution].flatMap(
    frame => {
      const productEntity = coreApi.entities.findProductEntity(frame.id);
      return productEntity ? [productEntity.productId] : [];
    }
  );

  dispatch(addInvalidFrames(invalidFrames));
  if (invalidFrames.length > 0) {
    dispatch(showUnavailableFramesMenu(true));
  }
  coreApi.scene?.setSceneHasUnavailableProducts(invalidProducts.length > 0);
};

// Selectors
export const getSelectedEntity = (state: RootState) =>
  state.core.selectedEntities[0] as ProductEntity | undefined; // Currently we only allow one selection
export const getSelectedEntities = (state: RootState) => state.core.selectedEntities;

export const getProductEntities = (state: RootState) => state.core.productEntities;
export const getShoppingBagEntities = (state: RootState) => state.core.shoppingBagEntities;

export const getConflicts = (state: RootState) => state.core.conflicts;
export const getInvalidProducts = (state: RootState) => state.core.invalidProducts;
export const getInvalidFrames = (state: RootState) => state.core.invalidFrames;
export const getReplacedFrames = (state: RootState) => state.core.replacedFrames;

export default coreSlice.reducer;
